import { Calendar, Film, Sparkles, BarChart3 } from 'lucide-react';
import { LandingTemplate, type LandingData } from '../components/LandingTemplate';
import { LANDING_GUIDES } from '../lib/landingGuides';

const data: LandingData = {
  seo: {
    title: 'Programar publicaciones en Instagram desde el ordenador · Zettapost',
    description:
      'Cómo programar posts, carruseles y reels en Instagram: desde la app y desde el ordenador con Zettapost. Guía paso a paso. Prueba 3 días gratis.',
    canonical: 'https://zettapost.com/programar-instagram',
  },
  hero: {
    eyebrow: 'Programar Instagram',
    headlinePre: 'Programa Instagram desde el ordenador',
    headlineAccent: 'en segundos.',
    subhead:
      'Publica posts, Reels y Stories en todas tus cuentas de Instagram a la hora que tú decidas. Sin tener que estar pendiente del móvil, sin notificaciones a medianoche y sin abrir 5 pestañas.',
    bullets: [
      'Posts, Reels y Stories nativos',
      'API oficial de Meta',
      'IA que escribe el caption',
      '3 días gratis',
    ],
  },
  why: {
    eyebrow: '¿Por qué Zettapost?',
    title: 'Programar Instagram sin estrés, como debería ser.',
    subtitle:
      'Pensado para community managers, agencias, marcas y creadores que quieren publicar en Instagram a su ritmo, no al ritmo de la app.',
    items: [
      {
        Icon: Calendar,
        title: 'Programa cuándo quieras',
        description:
          'Elige fecha y hora exacta o programa toda la semana con un par de clics. Zettapost publica automáticamente, también de noche y los fines de semana.',
      },
      {
        Icon: Film,
        title: 'Posts, Reels y Stories',
        description:
          'Soportamos los tres formatos nativos de Instagram con la API oficial de Meta. Vídeo vertical, carruseles y stories sin trampas ni recordatorios al móvil.',
      },
      {
        Icon: Sparkles,
        title: 'IA que escribe por ti',
        description:
          'Sube la imagen o vídeo y nuestra IA te genera el caption con el tono de tu marca y los hashtags que mejor encajan. Tú revisas, ajustas y publicas.',
      },
      {
        Icon: BarChart3,
        title: 'Sabe qué funcionó',
        description:
          'Métricas reales de cada post (likes, alcance, guardados, comentarios) traídas directamente desde Instagram para que veas qué contenido te trae más comunidad.',
      },
    ],
  },
  guide: LANDING_GUIDES.instagram,
  faqs: [
    {
      q: '¿Cómo conecto mi cuenta de Instagram?',
      a: 'Conectamos con la API oficial de Meta vía Facebook Login. Necesitas tener tu cuenta de Instagram en modo profesional (Empresa o Creador) y vinculada a una Página de Facebook. Te guiamos paso a paso desde la app, son 2 minutos.',
    },
    {
      q: '¿Necesito tener Instagram Empresa o Creador?',
      a: 'Sí. La API oficial de Meta solo permite programar publicaciones desde cuentas en modo profesional. Cambiar a Empresa o Creador es gratis y se hace desde los ajustes de Instagram en menos de 30 segundos.',
    },
    {
      q: '¿Puedo programar Reels desde el ordenador?',
      a: 'Sí. Subes el vídeo vertical, eliges la portada y la hora de publicación y Zettapost se encarga del resto. Sin notificaciones al móvil ni "trucos" tipo recordatorios.',
    },
    {
      q: '¿Y Stories?',
      a: 'También. Soportamos imágenes y vídeos verticales 9:16 para Stories. Los stickers interactivos avanzados (encuestas, preguntas, cuenta atrás) siguen añadiéndose desde el móvil, pero la programación, la imagen y el copy se gestionan desde Zettapost.',
    },
    {
      q: '¿Puedo conectar varias cuentas de Instagram?',
      a: 'Sí. Los planes para marcas y agencias permiten conectar varias cuentas de Instagram en paralelo y agruparlas por marca, con sus colores y tono de voz propios. Ideal para gestionar múltiples clientes desde un mismo panel.',
    },
    {
      q: '¿Qué pasa si una publicación falla?',
      a: 'Te avisamos al momento con el error concreto que devuelve Instagram (carácter inválido, sesión caducada, vídeo demasiado largo, etc.) y te dejamos reintentar con un solo clic. Nada se queda en silencio.',
    },
    {
      q: '¿Hay límite de publicaciones al mes?',
      a: 'No. Ningún plan limita el número de publicaciones al mes — programa todas las que necesites. Los planes se diferencian por el número de cuentas conectadas y por funciones avanzadas como la IA o los informes.',
    },
  ],
  finalCta: {
    title: 'Programa tu próximo post',
    titleAccent: 'en menos de 2 minutos.',
    description: 'Crea tu cuenta y conecta tu Instagram gratis durante 3 días.',
  },
  related: [
    { to: '/programar-tiktok', label: 'Programar TikTok' },
    { to: '/programar-linkedin', label: 'Programar LinkedIn' },
    { to: '/precios', label: 'Ver precios' },
    { to: '/blog', label: 'Blog' },
  ],
};

export function ProgramarInstagram() {
  return <LandingTemplate data={data} />;
}
